import type { ReactNode } from 'react'

import type { AttributionReport } from '@/api/attribution'
import type { DeltaPresentation } from '@/components/dataQuality'
import { DeltaBadge } from '@/components/DeltaBadge'
import type { Polarity } from '@/design'

import { NotTrackedBadge, UnattributedBadge } from './AttributionBadges'
import type { RowKind } from './definitions'

import { delta } from './format'

/** A figure with its previous-period change beneath it when the report is compared. */
export function Figure({
  value,
  current,
  previous,
  polarity = 'up_good',
  comparison,
}: {
  value: string
  current: number
  previous: number | null
  polarity?: Polarity
  comparison?: DeltaPresentation
}) {
  const change = previous === null ? null : delta(current, previous, polarity)
  return (
    <span className="attr-figure">
      <span>{value}</span>
      {change ? <DeltaBadge className="attr-delta" text={change.text} tone={change.tone} presentation={comparison} /> : null}
    </span>
  )
}

/** A figure's source, bound once per table: its short label, and the id of the legend entry that explains it. */
export interface SourceRef {
  source: string
  describedBy: string
}

/**
 * A cell carrying its own source, so a figure read alone still says whose it is: a
 * tooltip for the pointer, and the legend entry that names the source with its full
 * caveat (`aria-describedby`) for keyboard, touch and screen-reader readers.
 */
export function Sourced({ by, children }: { by: SourceRef; children: ReactNode }) {
  return (
    <span title={by.source} data-source={by.source} aria-describedby={by.describedBy}>
      {children}
    </span>
  )
}

/** The Not tracked row's name: its neutral badge alone, whose label is the name — in every table on the page. */
export function NotTrackedName({ lookbackDays }: { lookbackDays: number }) {
  return (
    <span className="attr-name" data-not-tracked>
      <NotTrackedBadge lookbackDays={lookbackDays} />
    </span>
  )
}

/**
 * A table's first cell: the group's name; on the unattributed row the badge explaining
 * what it holds; on the Not tracked row `NotTrackedName`.
 */
export function NameCell({ label, kind, report }: { label: string; kind: RowKind; report: AttributionReport }) {
  if (kind === 'group') return <>{label}</>
  if (kind === 'notTracked') return <NotTrackedName lookbackDays={report.lookbackDays} />
  return (
    <span className="attr-name">
      <span>{label}</span>
      <UnattributedBadge report={report} />
    </span>
  )
}
